import { type DefLookup } from "./catalog";
import { isFuseBlockLike } from "./auto-fuse";
import { isPassthroughComponent, type SimulateContext } from "./cable-current";
import { dcChargingPortGroups, dcPortTerminalGroups, isPvTerminalId } from "./component-ports";
import { buildDcCircuitTopology, dcInputPorts, type DcPort } from "./dc-circuit-topology";
import { effectiveTerminals } from "./effective";
import { computePvCurrentAnalysis } from "./pv-currents";
import { buildGraph, splitV, type Graph, type Vertex } from "./diagnostics/graph";
import { resolveTerminalPotentials, type TerminalPotentials } from "./terminal-voltage";
import { terminalPolarity } from "./terminal-snap";
import type {
  Cable,
  ComponentDefinition,
  System,
  SystemNode,
  Terminal,
} from "./types";
import {
  DEFAULT_NOMINAL_V,
  resolveVoltageDomains,
  voltageBridgedTerminals,
  type VoltageDomainMap,
} from "./voltage-domain";

/** Default nominal voltage. Prefer {@link resolveCableNominalVoltage}, which
 *  walks battery domains and so gets a 48 V + 12 V boat right. */
export const SYSTEM_BUS_V = DEFAULT_NOMINAL_V;

// Undefined means static sizing; live contexts must not be retained strongly.
const STATIC_VOLTAGE_STATE: SimulateContext = { componentStates: {} };

type CableVoltageContext = {
  graph: Graph;
  potentials: TerminalPotentials;
  circuitVoltages: Map<string, number>;
  resolved: WeakMap<Cable, number>;
};
// Battery-only edits may preserve cable identities. Include the nodes and
// lookup as well, so changing a source voltage cannot reuse the old answer.
// Live state gets its own entry: selector positions can change the supply.
const cableNominalCache = new WeakMap<Record<string, SystemNode>, WeakMap<Cable[], Map<DefLookup, WeakMap<SimulateContext, CableVoltageContext>>>>();

function voltageContext(nodesById: Record<string, SystemNode>, cables: Cable[], lookup: DefLookup, simulate?: SimulateContext): CableVoltageContext {
  let byCables = cableNominalCache.get(nodesById);
  if (!byCables) { byCables = new WeakMap(); cableNominalCache.set(nodesById, byCables); }
  let byLookup = byCables.get(cables);
  if (!byLookup) { byLookup = new Map(); byCables.set(cables, byLookup); }
  let byState = byLookup.get(lookup);
  if (!byState) { byState = new WeakMap(); byLookup.set(lookup, byState); }
  const stateKey = simulate ?? STATIC_VOLTAGE_STATE;
  let context = byState.get(stateKey);
  if (!context) {
    const system = { name: "", version: 0, nodes: Object.values(nodesById), cables, zoneDistances: {} };
    const graph = buildGraph(system, lookup, { excludeSense: true, simulate });
    const potentials = resolveTerminalPotentials(system, lookup, graph, simulate);
    context = { graph, potentials,
      circuitVoltages: completeCircuitVoltages(system, nodesById, lookup, potentials, simulate),
      resolved: new WeakMap() };
    byState.set(stateKey, context);
  }
  return context;
}

/** A common negative is a reference bond, not proof that every attached cable
 * belongs to the PV loop. Cache complete DC/PV cable memberships once per
 * snapshot. Deterministic DC routes are sufficient only on bridge cables:
 * parallel copper may have other memberships that a single witness omits. */
function completeCircuitVoltages(
  system: System, nodes: Record<string, SystemNode>, lookup: DefLookup,
  potentials: TerminalPotentials, simulate?: SimulateContext,
): Map<string, number> {
  const result = new Map<string, number>();
  const hasSolar = system.nodes.some(node => node.kind === "component" &&
    lookup(node.definitionId, node.customDefinition)?.category === "solar");
  const topology = buildDcCircuitTopology(system, lookup, simulate);
  // Complete DC routes establish ownership even when no PV array exists.
  const pv = hasSolar ? computePvCurrentAnalysis(nodes, system.cables, lookup) : undefined;
  const witnesses = new Map<string, Set<number>>();
  const unresolvedRails = new Set<string>();
  const add = (id: string, value: number) => {
    const values = witnesses.get(id) ?? new Set<number>();
    values.add(value); witnesses.set(id, values);
  };
  const ports: Array<DcPort & { output?: true }> = dcInputPorts(system, lookup);
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const declared = lookup(node.definitionId, node.customDefinition);
    if (!declared || !["charger", "load", "source", "engine"].includes(declared.category)) continue;
    const def = { ...declared, terminals: effectiveTerminals(declared, node) };
    for (const [index, terms] of dcChargingPortGroups(def).entries()) ports.push({
      id: `${node.id}:output:${index}`, nodeId: node.id, output: true,
      positive: terms.filter(t => t.polarity === "positive").map(t => `${node.id}:${t.id}`),
      negative: terms.filter(t => t.polarity !== "positive").map(t => `${node.id}:${t.id}`),
    });
  }
  for (const cable of system.cables) if (topology.invalidCableIds.has(cable.id)) {
    unresolvedRails.add(topology.railId(`${cable.fromNodeId}:${cable.fromTerminalId}`));
    unresolvedRails.add(topology.railId(`${cable.toNodeId}:${cable.toTerminalId}`));
  }
  for (const port of ports) {
    // PV inputs get their complete string voltage from the array analysis;
    // a controller's independent battery/vehicle ports remain DC witnesses.
    if (port.positive.some(vertex => isPvTerminalId(splitV(vertex).terminalId))) continue;
    const resolution = topology.resolvePort(port);
    let unresolved = resolution.unsupported.length > 0 || resolution.incomplete.length > 0;
    if (!resolution.routes.length && !port.output) unresolved = true;
    for (const route of resolution.routes) {
      const volts = potentials.voltageBetween(route.positiveTerminal, route.negativeTerminal);
      if (volts == null || !Number.isFinite(volts) || volts <= 0) { unresolved = true; continue; }
      for (const step of [...route.positive, ...route.negative, ...route.series]) add(step.cableId, volts);
    }
    // A converter with no external battery is still a declared source for
    // its loads; having no charging route itself does not invalidate them.
    if (unresolved) for (const vertex of [...port.positive, ...port.negative]) {
      unresolvedRails.add(topology.railId(vertex));
    }
  }
  for (const circuit of pv?.circuits ?? []) for (const id of [...circuit.pos, ...circuit.neg]) add(id, circuit.nominalV);
  for (const cable of system.cables) {
    const values = witnesses.get(cable.id);
    if (!values?.size || pv?.unresolvedCableIds.has(cable.id) || topology.invalidCableIds.has(cable.id)) continue;
    const from = `${cable.fromNodeId}:${cable.fromTerminalId}`;
    const to = `${cable.toNodeId}:${cable.toTerminalId}`;
    if (unresolvedRails.has(topology.railId(from)) || unresolvedRails.has(topology.railId(to))) continue;
    const volts = [...values];
    if (volts.some(value => Math.abs(value - volts[0]) > 1e-6)) continue;
    // Removing a bridge disconnects its endpoints. An alternate physical
    // path makes the deterministic route set insufficient for this cable.
    const seen = new Set([from]), queue = [from];
    for (let i = 0; i < queue.length && !seen.has(to); i++) {
      for (const edge of topology.graph.adj.get(queue[i]) ?? []) {
        if ((edge.via.kind === "cable" && edge.via.cableId === cable.id) || seen.has(edge.vertex)) continue;
        seen.add(edge.vertex); queue.push(edge.vertex);
      }
    }
    if (!seen.has(to)) result.set(cable.id, volts[0]);
  }
  return result;
}

/** A load/converter branch owns its terminal-pair voltage even through a fuse
 * or unbranched switch run. Stop at branches and sources: a shared trunk may
 * serve several voltages, so it retains the domain fallback below. */
function branchPortVoltage(cable: Cable, nodes: Record<string, SystemNode>, lookup: DefLookup, context: CableVoltageContext): number | undefined {
  const walk = (start: Vertex): number | undefined => {
    let vertex = start;
    let incoming = cable.id;
    const visited = new Set<Vertex>();
    while (!visited.has(vertex)) {
      visited.add(vertex);
      const { nodeId, terminalId } = splitV(vertex);
      const node = nodes[nodeId];
      if (node?.kind !== "component") return undefined;
      const def = lookup(node.definitionId, node.customDefinition);
      if (!def) return undefined;
      if (def.category === "load" || def.category === "charger" || def.category === "engine") {
        const groups = [...dcPortTerminalGroups(def, "input"), ...dcPortTerminalGroups(def, "output")];
        const volts: number[] = [];
        for (const group of groups.filter(g => g.some(t => t.id === terminalId))) {
          for (const p of group.filter(t => t.polarity === "positive")) for (const n of group.filter(t => t.polarity !== "positive")) {
            const value = context.potentials.voltageBetween(`${nodeId}:${p.id}`, `${nodeId}:${n.id}`);
            if (value != null && value > 0) volts.push(value);
          }
        }
        const unique = [...new Set(volts)];
        return unique.length === 1 ? unique[0] : undefined;
      }
      if (!isPassthroughComponent(def) && !isFuseBlockLike(def)) return undefined;
      const group = new Set<Vertex>([vertex]);
      const queue = [vertex];
      for (let i = 0; i < queue.length; i++) for (const { vertex: next, via } of context.graph.adj.get(queue[i]) ?? []) {
        if (via.kind !== "intra" || group.has(next)) continue;
        group.add(next); queue.push(next);
      }
      const exits = new Map<string, Vertex>();
      for (const member of group) for (const { vertex: next, via } of context.graph.adj.get(member) ?? []) {
        if (via.kind === "cable" && via.cableId !== incoming) exits.set(via.cableId, next);
      }
      if (exits.size !== 1) return undefined;
      [incoming, vertex] = [...exits][0];
    }
    return undefined;
  };
  const from = walk(`${cable.fromNodeId}:${cable.fromTerminalId}`);
  const to = walk(`${cable.toNodeId}:${cable.toTerminalId}`);
  if (from != null && to != null && Math.abs(from - to) > 1e-6) return undefined;
  return from ?? to;
}

type TermKey = `${string}:${string}`;
const key = (nodeId: string, termId: string): TermKey => `${nodeId}:${termId}`;
const parseKey = (k: TermKey): { nodeId: string; termId: string } => {
  const idx = k.indexOf(":");
  return { nodeId: k.slice(0, idx), termId: k.slice(idx + 1) };
};

/** (node, terminal) pairs at the same potential. Leaves are not spanned — a
 *  battery's + and − are different potentials, not one node. */
function regionOf(
  seedNodeId: string,
  seedTermId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): Set<TermKey> {
  const visited = new Set<TermKey>();
  const queue: Array<{ nodeId: string; termId: string }> = [
    { nodeId: seedNodeId, termId: seedTermId },
  ];
  while (queue.length) {
    const { nodeId, termId } = queue.shift()!;
    const k = key(nodeId, termId);
    if (visited.has(k)) continue;
    visited.add(k);

    for (const c of cables) {
      if (c.fromNodeId === nodeId && c.fromTerminalId === termId) {
        queue.push({ nodeId: c.toNodeId, termId: c.toTerminalId });
      } else if (c.toNodeId === nodeId && c.toTerminalId === termId) {
        queue.push({ nodeId: c.fromNodeId, termId: c.fromTerminalId });
      }
    }

    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    for (const t of voltageBridgedTerminals(node, def, termId)) {
      queue.push({ nodeId, termId: t });
    }
  }
  return visited;
}

/**
 * PV subgraph reachable from a cable. Crosses solar panels themselves, so a
 * series link ties both into one string; stops at chargers/batteries/loads,
 * whose boundary terminals are still included.
 */
function collectPvSubgraph(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): Set<TermKey> {
  const visited = new Set<TermKey>();
  const queue: Array<{ nodeId: string; termId: string }> = [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ];
  while (queue.length) {
    const { nodeId, termId } = queue.shift()!;
    const k = key(nodeId, termId);
    if (visited.has(k)) continue;
    visited.add(k);

    for (const c of cables) {
      if (c.fromNodeId === nodeId && c.fromTerminalId === termId) {
        queue.push({ nodeId: c.toNodeId, termId: c.toTerminalId });
      } else if (c.toNodeId === nodeId && c.toTerminalId === termId) {
        queue.push({ nodeId: c.fromNodeId, termId: c.fromTerminalId });
      }
    }

    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    if (def.category === "solar") {
      for (const t of def.terminals) {
        if (t.id !== termId) queue.push({ nodeId, termId: t.id });
      }
      continue;
    }
    for (const t of voltageBridgedTerminals(node, def, termId)) {
      queue.push({ nodeId, termId: t });
    }
  }
  return visited;
}

type PanelTerm = {
  nodeId: string;
  def: ComponentDefinition;
  posTermId: string;
  negTermId: string;
};

function findPanelWithPosInRegion(
  region: Set<TermKey>,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
  exclude: Set<string>,
): PanelTerm | undefined {
  for (const k of region) {
    const { nodeId, termId } = parseKey(k);
    if (exclude.has(nodeId)) continue;
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.category !== "solar") continue;
    const term = def.terminals.find((t: Terminal) => t.id === termId);
    if (!term || terminalPolarity(term) !== "positive") continue;
    const negTerm = def.terminals.find(
      (t: Terminal) => terminalPolarity(t) === "negative",
    );
    if (!negTerm) continue;
    return {
      nodeId,
      def,
      posTermId: termId,
      negTermId: negTerm.id,
    };
  }
  return undefined;
}

/** Sums each panel's `voltage` along a series string. Parallel panels share a
 *  region and count once, so 2S2P returns one string's voltage. */
function walkPanelChain(
  startNodeId: string,
  startTermId: string,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
): number {
  let region = regionOf(startNodeId, startTermId, nodesById, cables, defLookup);
  const seen = new Set<string>();
  let v = 0;
  // `seen` already terminates any malformed loop; the cap is belt and braces.
  let safety = 100;
  while (safety-- > 0) {
    const panel = findPanelWithPosInRegion(region, nodesById, defLookup, seen);
    if (!panel) break;
    seen.add(panel.nodeId);
    v += panel.def.voltage ?? SYSTEM_BUS_V;
    region = regionOf(panel.nodeId, panel.negTermId, nodesById, cables, defLookup);
  }
  return v;
}

/**
 * Nominal voltage of a cable's circuit: the PV string's summed Vmp when the
 * cable is on a charger's PV side, else the cable's own battery domain, else
 * {@link SYSTEM_BUS_V}. Drives the V-drop budget, so it is a percentage of this
 * and not of 12 V.
 *
 * Pass `precomputedDomains` when resolving many cables — each call otherwise
 * rebuilds the whole domain map.
 */
export function resolveCableNominalVoltage(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  precomputedDomains?: VoltageDomainMap,
  simulate?: SimulateContext,
): number {
  const context = voltageContext(nodesById, cables, defLookup, simulate);
  if (precomputedDomains === undefined) {
    const cached = context.resolved.get(cable);
    if (cached !== undefined) return cached;
    const result = resolveCableNominalVoltageImpl(
      cable,
      nodesById,
      cables,
      defLookup,
      context,
    );
    context.resolved.set(cable, result);
    return result;
  }
  return resolveCableNominalVoltageImpl(
    cable,
    nodesById,
    cables,
    defLookup,
    context,
    precomputedDomains,
  );
}

function resolveCableNominalVoltageImpl(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  cables: Cable[],
  defLookup: DefLookup,
  context: CableVoltageContext,
  precomputedDomains?: VoltageDomainMap,
): number {
  const circuitVoltage = context.circuitVoltages.get(cable.id);
  if (circuitVoltage != null) return circuitVoltage;

  const subgraph = collectPvSubgraph(cable, nodesById, cables, defLookup);
  let chargerPosTerm: { nodeId: string; termId: string } | undefined;
  let hasPanel = false;
  for (const k of subgraph) {
    const { nodeId, termId } = parseKey(k);
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    if (def.category === "solar") {
      hasPanel = true;
      continue;
    }
    if (def.category === "charger") {
      const term = def.terminals.find((t: Terminal) => t.id === termId);
      if (
        term?.direction === "input" &&
        terminalPolarity(term) === "positive" &&
        !chargerPosTerm
      ) {
        chargerPosTerm = { nodeId, termId };
      }
    }
  }
  if (chargerPosTerm && hasPanel) {
    const v = walkPanelChain(
      chargerPosTerm.nodeId,
      chargerPosTerm.termId,
      nodesById,
      cables,
      defLookup,
    );
    if (v > 0) return v;
  }

  const branchVoltage = branchPortVoltage(cable, nodesById, defLookup, context);
  if (branchVoltage != null) return branchVoltage;

  // The minimal `System`-shaped argument spares callers threading a whole System.
  const domains =
    precomputedDomains ??
    resolveVoltageDomains(
      { name: "", nodes: Object.values(nodesById), cables, zoneDistances: {}, version: 0 } as System,
      defLookup,
      SYSTEM_BUS_V,
    );
  const dom = domains.domainByCable.get(cable.id);
  if (dom?.hasSource) return dom.nominalV;

  return SYSTEM_BUS_V;
}
